/**
 * AI frame generator modal — a mini 3-step slider (same language as the
 * outer /new shell):
 *
 *   1. Layout    — pick a blank template (real photo-window layouts)
 *   2. Describe  — one rich prompt box → generate
 *   3. Preview   — iterate with follow-up prompts, then save
 *
 * Under the hood this is the dashboard's blank-template chat flow
 * (/api/ai/threads + messages); the green placeholder mechanics stay
 * completely hidden — the server keys the slot windows transparent when
 * saving. Auth is ensured by the caller; a fresh Google account has daily
 * free generations.
 */

import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaSpinner, FaTimes, FaPenNib, FaCheck, FaArrowLeft } from 'react-icons/fa'
import {
  fetchBlankTemplates,
  createFrameThread,
  sendFramePrompt,
  saveChatFrame,
  type BlankTemplate,
  type CatalogFrame
} from '../onboardingApi'
import { useOnboardingStore } from '../onboardingStore'
import { useCopy } from '../regionCopy'

interface AiFrameModalProps {
  boothName: string
  onClose: () => void
  onSaved: (frame: CatalogFrame) => void
}

type Step = 'layout' | 'describe' | 'preview'

export const AiFrameModal: React.FC<AiFrameModalProps> = ({ boothName, onClose, onSaved }) => {
  const c = useCopy()
  const [step, setStep] = useState<Step>('layout')
  const [templates, setTemplates] = useState<BlankTemplate[] | null>(null)
  const [template, setTemplate] = useState<BlankTemplate | null>(null)
  // Prefill from the booth pipeline: the generated spec carries a ready
  // framePrompt (or we compose one from its tags/mood) so the frame starts in
  // the same visual world as the welcome design — even before sign-in.
  const [prompt, setPrompt] = useState(() => {
    const spec = useOnboardingStore.getState().spec
    if (!spec) return ''
    if (spec.framePrompt && spec.framePrompt.trim()) return spec.framePrompt.trim()
    const bits = [...(spec.frameTags ?? []), spec.filterMood].filter(Boolean)
    return bits.length ? `${bits.join(', ')}` : ''
  })
  const [editPrompt, setEditPrompt] = useState('')
  const [threadId, setThreadId] = useState<string | null>(null)
  const [generation, setGeneration] = useState<{ generationId: string; imageUrl: string } | null>(
    null
  )
  const [busy, setBusy] = useState<'generate' | 'iterate' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchBlankTemplates()
      .then((t) => {
        if (!cancelled) setTemplates(t)
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleGenerate = async (): Promise<void> => {
    if (!template || prompt.trim().length < 3 || busy) return
    setBusy('generate')
    setError(null)
    try {
      const tid = threadId ?? (await createFrameThread(template))
      setThreadId(tid)
      const res = await sendFramePrompt(tid, prompt.trim())
      setGeneration(res)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : c.frameGenerationFailed)
    } finally {
      setBusy(null)
    }
  }

  const handleIterate = async (): Promise<void> => {
    if (!threadId || editPrompt.trim().length < 3 || busy) return
    setBusy('iterate')
    setError(null)
    try {
      const res = await sendFramePrompt(threadId, editPrompt.trim())
      setGeneration(res)
      setEditPrompt('')
    } catch (err) {
      setError(err instanceof Error ? err.message : c.frameEditFailed)
    } finally {
      setBusy(null)
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!threadId || !generation || busy) return
    setBusy('save')
    setError(null)
    try {
      const frame = await saveChatFrame({
        threadId,
        generationId: generation.generationId,
        name: boothName ? `${boothName} frame` : undefined
      })
      onSaved(frame)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : c.frameSaveFailed)
      setBusy(null)
    }
  }

  const stepIndex = step === 'layout' ? 0 : step === 'describe' ? 1 : 2

  // PORTAL to <body>: this modal is rendered inside the step slider, whose
  // `transform: translateX(...)` makes position:fixed resolve against the
  // TRANSFORMED ancestor (then overflow-hidden clips it) — without the
  // portal the modal opens fully off-screen and "nothing happens".
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#101014] shadow-2xl">
        {/* Header + mini step rail */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="flex items-center gap-3">
            <FaPenNib className="text-blueSparkle" />
            <span className="text-sm font-semibold text-whiteout">{c.yourOwnFrame}</span>
            <div className="ml-2 flex items-center gap-1.5">
              {[c.modalLayout, c.modalDescribe, c.modalPreview].map((label, i) => (
                <span
                  key={label}
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                    i === stepIndex
                      ? 'bg-blueSparkle text-whiteout'
                      : i < stepIndex
                        ? 'text-whiteout/60'
                        : 'text-whiteout/30'
                  }`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={c.cancel}
            className="text-whiteout/50 hover:text-whiteout"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto soft-scrollbar p-5">
          {step === 'layout' && (
            <>
              <p className="mb-3 text-sm text-whiteout/60">
                {c.pickPhotoCount}
              </p>
              {!templates ? (
                <div className="flex items-center justify-center py-12 text-whiteout/50">
                  <FaSpinner className="animate-spin text-xl" />
                </div>
              ) : templates.length === 0 ? (
                <p className="py-10 text-center text-sm text-whiteout/40">
                  {c.noLayouts}
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {templates.map((t) => (
                    <button
                      key={t.templateId}
                      type="button"
                      onClick={() => {
                        setTemplate(t)
                        setStep('describe')
                      }}
                      className={`group flex flex-col items-center gap-1.5 rounded-2xl border p-2 transition-colors ${
                        template?.templateId === t.templateId
                          ? 'border-blueSparkle bg-blueSparkle/10'
                          : 'border-white/10 bg-white/[0.03] hover:border-white/30'
                      }`}
                    >
                      <div className="flex aspect-[2/3] w-full items-center justify-center overflow-hidden rounded-lg bg-white/90 p-1">
                        {/* grayscale: the source previews mark photo slots in
                            keying green; grey placeholders read as "photos go
                            here" (matches the dashboard frame creator). */}
                        <img
                          src={t.previewUrl}
                          alt={t.placeholderShapeType}
                          loading="lazy"
                          className="max-h-full max-w-full object-contain opacity-90 grayscale"
                        />
                      </div>
                      <span className="text-[10px] text-whiteout/50">
                        {c.photosCount(t.placeholderCount)} ·{' '}
                        {t.placeholderShapeType.replace(/_/g, ' ')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 'describe' && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                {template && (
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-white/90 p-1">
                    <img
                      src={template.previewUrl}
                      alt={c.modalLayout}
                      className="h-full w-full object-contain grayscale"
                    />
                  </div>
                )}
                <p className="text-sm text-whiteout/60">
                  {c.describeFrame}
                </p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-3 focus-within:border-blueSparkle">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  maxLength={600}
                  placeholder={c.framePlaceholder}
                  className="w-full resize-none bg-transparent text-sm text-whiteout placeholder-whiteout/30 outline-none"
                  autoFocus
                />
              </div>
            </div>
          )}

          {step === 'preview' && generation && (
            <div className="flex flex-col items-center gap-4">
              <div className="max-h-[45vh] overflow-hidden rounded-xl ring-1 ring-white/15">
                <img
                  src={generation.imageUrl}
                  alt="Generated frame"
                  className="max-h-[45vh] w-auto object-contain"
                />
              </div>
              <div className="w-full rounded-2xl border border-white/15 bg-white/[0.04] p-3 focus-within:border-blueSparkle">
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  rows={2}
                  maxLength={400}
                  placeholder={c.editFramePlaceholder}
                  className="w-full resize-none bg-transparent text-sm text-whiteout placeholder-whiteout/30 outline-none"
                />
              </div>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/10 px-5 py-3">
          <button
            type="button"
            onClick={() => {
              if (step === 'describe') setStep('layout')
              else if (step === 'preview') setStep('describe')
              else onClose()
            }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs text-whiteout/60 hover:text-whiteout"
          >
            <FaArrowLeft className="text-[10px]" />
            {step === 'layout' ? c.cancel : c.back}
          </button>

          <div className="flex items-center gap-2">
            {step === 'describe' && (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={prompt.trim().length < 3 || busy !== null}
                className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-6 py-2 text-xs font-semibold text-whiteout disabled:opacity-40"
              >
                {busy === 'generate' && <FaSpinner className="animate-spin" />}
                {c.drawIt}
              </button>
            )}
            {step === 'preview' && (
              <>
                <button
                  type="button"
                  onClick={handleIterate}
                  disabled={editPrompt.trim().length < 3 || busy !== null}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-xs font-medium text-whiteout/80 hover:border-blueSparkle/60 disabled:opacity-40"
                >
                  {busy === 'iterate' && <FaSpinner className="animate-spin" />}
                  {c.applyEdit}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-6 py-2 text-xs font-semibold text-whiteout disabled:opacity-40"
                >
                  {busy === 'save' ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  {c.addToBooth}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AiFrameModal
